/**
 * filterBoxConfigs.ts — Config declarativa por sección de la caja de filtros (GH#24).
 *
 * UNA sola caja de filtros reusable (FilterBox) para Publicaciones, Estadísticas,
 * Data Health y cualquier otro listado. Este archivo define:
 *   1. Los TIPOS de la config (FilterBoxConfig y afines).
 *   2. Los LABELS i18n por dimensión (marca/sitio/categoría/subcategoría/segmento),
 *      extraídos 1:1 de los renderers de UnifiedFilterPanel para no cambiar el copy.
 *   3. Las CONSTANTES de config por sección (PUBLICACIONES / ESTADISTICAS /
 *      DATAHEALTH_HIDDEN), más un registry para lookup por sectionId.
 *
 * DECISIÓN (spec §3.4): la config por sección vive HARDCODEADA en el front, NO en
 * Firestore/DB. Es layout de UI estático por sección; el eje per-cliente (qué
 * dimensiones existen/son filtrables) se combina en runtime vía columnConfig + caps
 * de catalogMergeResolver (regla de composición: controlVisible = clientAllows AND
 * sectionConfig.enabled). Persistir esto en DB sería la trampa del constructor.
 */

import type { FilterDropdownLabels } from '../components/filters/FilterDropdown';

// ── Tipos ──────────────────────────────────────────────────────────────────

/** Dimensiones que se resuelven como dropdown multiselect (con search + counts). */
export type DimensionKey = 'brand' | 'site' | 'category' | 'subcategory' | 'segment';

/** Todos los controles posibles de la caja (dimensionales + numéricos + extras). */
export type FilterControlKey =
  | DimensionKey
  | 'price' | 'position' | 'discount'         // numéricos (segunda fila de Publicaciones)
  | 'globalSearch'                            // búsqueda global con pills
  | 'metric' | 'priceType' | 'period' | 'analysisLevel' | 'groupBy'; // extras Tendencias (Fase 3)

/** Fuente de datos que alimenta las options de los dropdowns dimensionales. */
export type FilterSource =
  | { kind: 'facets'; endpoint: 'tables/:t/facets'; includeHidden?: boolean } // Publicaciones/Data Health
  | { kind: 'inline'; from: 'trendData' };                                    // Tendencias (Fase 3)

/** Un control individual dentro de un renglón. `key` identifica el renderer. */
export interface FilterControl {
  key: FilterControlKey;
  enabled: boolean;
  /** Ancho relativo (solo dimensionales/numéricos). Si se omite → auto. */
  widthPct?: number;
  /** El modo/sección fija el valor y lo bloquea (UX: modos auto-rellenan). */
  readOnly?: boolean;
}

export interface FilterRow {
  fields: FilterControl[];
}

export interface FilterBoxConfig {
  /** 'publicaciones' | 'estadisticas' | 'datahealth.hidden' | ... */
  sectionId: string;
  source: FilterSource;
  /** Renglones ordenados; un renglón entero se apaga si todos sus fields enabled=false. */
  rows: FilterRow[];
  /** R3: si true, category/subcategory/segment se comportan como árbol dependiente. */
  treeMode?: boolean;
  /**
   * PT-#57 R2-4 — si true, los dropdowns NO muestran counts (total del header ni (N) por
   * opción), PERO el `meta.counts` sigue disponible para el cascade de treeMode. Se usa en
   * Tendencias (multi-toma): los counts cross-toma engañan, así que se ocultan del display,
   * pero la co-ocurrencia (qué subcategorías existen bajo la categoría elegida) SÍ acota las
   * options igual que Publicaciones/Estadísticas. FilterBox pasa meta=null al FilterDropdown
   * (apaga el display) y usa la meta real solo para `treeVisibleOptions`.
   */
  hideCounts?: boolean;
  /** Header con show/hide (default true). */
  collapsible?: boolean;
  /** Botón "Filtros guardados" (solo Publicaciones hoy). */
  showSavedFilters?: boolean;
}

/**
 * PT-#45 #1 — Grilla 4/2/1. Todo `widthPct` de la caja se normaliza a estos 3 valores
 * para que cada fila quede alineada a una grilla consistente en las 3 secciones:
 *   - `GRID_QUARTER` (25%) = fila de hasta 4 columnas (dimensionales cuando entran las 4).
 *   - `GRID_HALF`    (50%) = fila de 2 columnas (numéricos: precio/posición/descuento).
 *   - `GRID_FULL`   (100%) = fila de 1 columna (búsqueda global, o un único campo).
 * El renderer de FilterBox aplica `width: widthPct%` + `minWidth:180px` sobre un contenedor
 * `flex flex-wrap gap-4`, de modo que la grilla reflowea (wrap) en anchos chicos.
 */
export const GRID_QUARTER = 25;
export const GRID_THIRD = 33.33; // fila de 3 columnas (Tendencias: Cat/Subcat/Segmento)
export const GRID_HALF = 50;
export const GRID_FULL = 100;

// ── Labels i18n por dimensión (1:1 con UnifiedFilterPanel) ───────────────────

/**
 * Cada dimensión define su label + los sufijos del placeholder. Guardamos la clave
 * i18n y un fallback en español; FilterBox resuelve con t(key, fallback) para
 * preservar el multi-idioma existente (es/en/it/pt).
 */
export interface DimensionLabelSpec {
  /** Género gramatical de la dimensión → concuerda el "TODOS" del select-all (Todas/Todos). */
  gender: 'f' | 'm';
  /** Clave i18n del label del dropdown (encabezado). */
  labelKey: string;
  labelFallback: string;
  /** Placeholder cuando no hay selección, ej. "Todas las marcas". */
  allKey: string;
  allFallback: string;
  /** Sufijo singular (se prefija con "1 "). */
  singularKey: string;
  singularFallback: string;
  /** Sufijo plural (se prefija con el conteo). */
  pluralKey: string;
  pluralFallback: string;
}

/** Clave i18n compartida del placeholder de búsqueda dentro del dropdown. */
export const SEARCH_PLACEHOLDER_I18N = { key: 'filters.search_placeholder', fallback: 'Buscar...' };
/** Clave i18n compartida del texto "Sin opciones". */
export const NO_OPTIONS_I18N = { key: 'filters.no_options', fallback: 'Sin opciones' };
/** "Seleccionar todo" concordado por género (PT-#45 #10) — reutilizado por dimensión. */
export const SELECT_ALL_F_I18N = { key: 'filters.select_all_feminine', fallback: 'Todas' };
export const SELECT_ALL_M_I18N = { key: 'filters.select_all_masculine', fallback: 'Todos' };

/** Dimensiones que muestran "Sin opciones" cuando quedan sin valores (spec: sub/seg). */
export const DIMENSIONS_WITH_NO_OPTIONS: DimensionKey[] = ['subcategory', 'segment'];

export const DIMENSION_LABELS: Record<DimensionKey, DimensionLabelSpec> = {
  brand: {
    gender: 'f',
    labelKey: 'columns.marca', labelFallback: 'Marca',
    allKey: 'filters.all_brands', allFallback: 'Todas las marcas',
    singularKey: 'filters.selected_brand_singular', singularFallback: 'marca seleccionada',
    pluralKey: 'filters.selected_brands', pluralFallback: 'marcas seleccionadas',
  },
  site: {
    gender: 'm',
    labelKey: 'columns.sitio', labelFallback: 'Sitio',
    allKey: 'filters.all_sites', allFallback: 'Todos los sitios',
    singularKey: 'filters.selected_site_singular', singularFallback: 'sitio seleccionado',
    pluralKey: 'filters.selected_sites', pluralFallback: 'sitios seleccionados',
  },
  category: {
    gender: 'f',
    labelKey: 'columns.categoria', labelFallback: 'Categoría',
    allKey: 'filters.all_categories', allFallback: 'Todas las categorías',
    singularKey: 'filters.selected_category_singular', singularFallback: 'categoría seleccionada',
    pluralKey: 'filters.selected_categories', pluralFallback: 'categorías seleccionadas',
  },
  subcategory: {
    gender: 'f',
    labelKey: 'columns.subcategoria', labelFallback: 'Subcategoría',
    allKey: 'filters.all_subcategories', allFallback: 'Todas las subcategorías',
    singularKey: 'filters.selected_subcategory_singular', singularFallback: 'subcategoría seleccionada',
    pluralKey: 'filters.selected_subcategories', pluralFallback: 'subcategorías seleccionadas',
  },
  segment: {
    gender: 'm',
    labelKey: 'columns.segmento', labelFallback: 'Segmento',
    allKey: 'filters.all_segments', allFallback: 'Todos los segmentos',
    singularKey: 'filters.selected_segment_singular', singularFallback: 'segmento seleccionado',
    pluralKey: 'filters.selected_segments', pluralFallback: 'segmentos seleccionados',
  },
};

/**
 * Resuelve los labels de una dimensión a strings finales usando la función `t` de
 * i18next (o cualquier (key, fallback)=>string). Devuelve el shape que consume
 * FilterDropdown.
 */
export function resolveDimensionLabels(
  key: DimensionKey,
  t: (k: string, fallback?: string) => string,
): { label: string; labels: FilterDropdownLabels } {
  const spec = DIMENSION_LABELS[key];
  const selectAllSpec = spec.gender === 'f' ? SELECT_ALL_F_I18N : SELECT_ALL_M_I18N;
  return {
    label: t(spec.labelKey, spec.labelFallback),
    labels: {
      all: t(spec.allKey, spec.allFallback),
      singular: t(spec.singularKey, spec.singularFallback),
      plural: t(spec.pluralKey, spec.pluralFallback),
      searchPlaceholder: t(SEARCH_PLACEHOLDER_I18N.key, SEARCH_PLACEHOLDER_I18N.fallback),
      noOptions: t(NO_OPTIONS_I18N.key, NO_OPTIONS_I18N.fallback),
      selectAll: t(selectAllSpec.key, selectAllSpec.fallback),
    },
  };
}

/** Orden jerárquico de la cadena del árbol (spec §4.5). Se filtra por caps del cliente. */
export const TREE_LEVEL_ORDER: DimensionKey[] = ['category', 'subcategory', 'segment'];

export const DIMENSION_KEYS: DimensionKey[] = ['brand', 'site', 'category', 'subcategory', 'segment'];

export function isDimensionKey(key: FilterControlKey): key is DimensionKey {
  return (DIMENSION_KEYS as string[]).includes(key);
}

// ── Configs por sección ──────────────────────────────────────────────────────

/** PUBLICACIONES — la caja histórica: 5 dimensiones + numéricos + pills + saved filters. */
export const PUBLICACIONES_CONFIG: FilterBoxConfig = {
  sectionId: 'publicaciones',
  source: { kind: 'facets', endpoint: 'tables/:t/facets' },
  treeMode: true,
  collapsible: true,
  showSavedFilters: true,
  rows: [
    // Dimensionales: fila de hasta 4 al 25% (wrap si no entran). PT-#45 #1.
    { fields: [ { key: 'brand', enabled: true, widthPct: GRID_QUARTER }, { key: 'site', enabled: true, widthPct: GRID_QUARTER }, { key: 'category', enabled: true, widthPct: GRID_QUARTER }, { key: 'subcategory', enabled: true, widthPct: GRID_QUARTER } ] },
    // Segmento: fila dimensional propia. PT-#57 R2-1: el host inyecta DESCUENTO como
    // último item de la ÚLTIMA fila dimensional (para Samsung = Cat/Subcat/Segmento) → NO
    // hay fila suelta de discount ni duplicado de Segmento (R2-6 "NO INVENTES").
    { fields: [ { key: 'segment', enabled: true, widthPct: GRID_QUARTER } ] },
    // Numéricos: precio + posición 2-col al 50% (discount lo inyecta el host en fila dim).
    { fields: [ { key: 'price', enabled: true, widthPct: GRID_HALF }, { key: 'position', enabled: true, widthPct: GRID_HALF } ] },
    // Búsqueda global: 1-col al 100%.
    { fields: [ { key: 'globalSearch', enabled: true, widthPct: GRID_FULL } ] },
  ],
};

/** ESTADÍSTICAS — comparte dimensiones con Publicaciones pero sin posición ni pills. */
export const ESTADISTICAS_CONFIG: FilterBoxConfig = {
  sectionId: 'estadisticas',
  source: { kind: 'facets', endpoint: 'tables/:t/facets' },
  treeMode: true,
  collapsible: true,
  showSavedFilters: false,
  rows: [
    // Dimensionales al 25% (wrap): 4 caben en una fila, segment baja por wrap. PT-#45 #1.
    { fields: [ { key: 'brand', enabled: true, widthPct: GRID_QUARTER }, { key: 'site', enabled: true, widthPct: GRID_QUARTER }, { key: 'category', enabled: true, widthPct: GRID_QUARTER }, { key: 'subcategory', enabled: true, widthPct: GRID_QUARTER } ] },
    // Segmento: fila dimensional propia. PT-#57 R2-1: el host inyecta DESCUENTO como
    // último item de la última fila dimensional (no fila suelta ni Segmento duplicado).
    { fields: [ { key: 'segment', enabled: true, widthPct: GRID_QUARTER } ] },
    // Precio: 1-col al 50% (queda solo; sin Posición para acompañarlo).
    { fields: [ { key: 'price', enabled: true, widthPct: GRID_HALF } ] },
  ],
};

/**
 * DATA HEALTH (listado OCULTOS) — solo dimensionales para acotar el listado.
 * Fuente = facets con includeHidden=true (Data Health SÍ ve ocultos). El search de
 * texto lo aporta ListingHeader; acá no van price/position/discount/pills.
 */
export const DATAHEALTH_HIDDEN_CONFIG: FilterBoxConfig = {
  sectionId: 'datahealth.hidden',
  source: { kind: 'facets', endpoint: 'tables/:t/facets', includeHidden: true },
  treeMode: true,
  collapsible: true,
  showSavedFilters: false,
  rows: [
    // Dimensionales al 25% (wrap): 4 caben en una fila, segment baja por wrap. PT-#45 #1.
    { fields: [ { key: 'brand', enabled: true, widthPct: GRID_QUARTER }, { key: 'category', enabled: true, widthPct: GRID_QUARTER }, { key: 'subcategory', enabled: true, widthPct: GRID_QUARTER }, { key: 'segment', enabled: true, widthPct: GRID_QUARTER } ] },
  ],
};

/**
 * TENDENCIAS — MISMO layout dimensional que Publicaciones (GH#24 Fase 3): las 5
 * dimensiones son COMBINABLES (Samsung pidió cruzar Marca + Categoría + Subcategoría +
 * Segmento). Antes Category y Brand eran XOR según `groupBy` (el eje del gráfico); eso
 * se decoupleó: `groupBy` decide SOLO el eje de agregación, no qué filtros están activos.
 *
 * Fuente = inline (las options salen de endpoints distinct-* que el host ya carga,
 * universo multi-toma/período — NO facets per-toma). Con counts por dimensión el host
 * habilita treeMode (cascade category→subcategory→segment igual que Publicaciones).
 * Los caps per-cliente (Adidas sin subcategoría/segmento) colapsan esos dropdowns vía
 * `enabled` en la DimensionState, idéntico a UnifiedFilterPanel.
 *
 * Row 0/1 (analysisLevel + groupBy) y los controles no dimensionales
 * (métrica/tipo de precio/temporal/Confirmar) los inyecta el host vía `slots`, dentro
 * de la MISMA caja y con el MISMO estilo visual.
 */
export const TENDENCIAS_CONFIG: FilterBoxConfig = {
  sectionId: 'tendencias',
  source: { kind: 'inline', from: 'trendData' },
  treeMode: true,
  // PT-#57 R2-4 — cascade + counts IGUAL que Pubs/Stats. La taxonomía CATEG/SUBCATEG/
  // SEGMENTO es estática: el count del facet real de la toma (trendFacets) es tan válido
  // como en Publicaciones/Estadísticas y se MUESTRA. Al cambiar categoría, subcategoría Y
  // segmento recalculan sus counts en vivo (trendFacets cross-filtra las 3 dimensiones).
  // (Antes: hideCounts:true ocultaba los counts por miedo a "multi-toma engaña" → mataba
  //  la señal de cascade que el usuario espera ver, divergiendo de Pubs/Stats. Revertido.)
  collapsible: true,
  showSavedFilters: false,
  // PT-#57 R2-3 — layout EXACTO pedido por Gabriel: TODOS los campos al 25%, alineados
  // de izq→der, cada renglón separado (NO wrap 4-across mezclando dimensiones):
  //   1) Nivel de Análisis 25% / Agrupar Por 25%
  //   2) Marca 25% / Sitio 25%
  //   3) Categoría 25% / Subcategoría 25% / Segmento 25%
  //   4) Métrica 25% / Tipo de Precio 25%
  //   5) Acotar por 25% / Período 25% / Desde 25% / Hasta 25% (sub-grilla del host)
  rows: [
    { fields: [
      { key: 'analysisLevel', enabled: true, widthPct: GRID_QUARTER },
      { key: 'groupBy', enabled: true, widthPct: GRID_QUARTER },
    ] },
    { fields: [
      { key: 'brand', enabled: true, widthPct: GRID_QUARTER },
      { key: 'site', enabled: true, widthPct: GRID_QUARTER },
    ] },
    { fields: [
      { key: 'category', enabled: true, widthPct: GRID_QUARTER },
      { key: 'subcategory', enabled: true, widthPct: GRID_QUARTER },
      { key: 'segment', enabled: true, widthPct: GRID_QUARTER },
    ] },
    { fields: [
      { key: 'metric', enabled: true, widthPct: GRID_QUARTER },
      { key: 'priceType', enabled: true, widthPct: GRID_QUARTER },
    ] },
    // Acotado temporal: slot full-width que rinde su sub-grilla a 25% cada control.
    { fields: [
      { key: 'period', enabled: true, widthPct: GRID_FULL },
    ] },
  ],
};

/**
 * EXPORTAR (PT-#61) — la MISMA caja de filtros que TENDENCIAS, no la de Publicaciones.
 *
 * EXPORTAR es multi-toma (concatena filas de N tomas resueltas por cantidad o período),
 * así que reusa el modelo de Tendencias: fuente `inline` (options de los endpoints
 * `distinct-*`, universo multi-toma — NO facets per-toma), 5 dimensiones COMBINABLES con
 * cascade `treeMode`, y el alcance temporal como slot (`period`) DENTRO de la caja.
 *
 * A DIFERENCIA de Tendencias, EXPORTAR exporta FILAS CRUDAS (no una matriz agregada), por
 * eso OMITE los controles de agregación de Trends (`analysisLevel`/`groupBy`/`metric`/
 * `priceType`): serían controles muertos. Solo van las dimensiones + el alcance temporal.
 * Ver `docs/pendientes/PT61-exportar-caja-filtros-trends.md`.
 */
export const EXPORTAR_CONFIG: FilterBoxConfig = {
  sectionId: 'exportar',
  source: { kind: 'inline', from: 'trendData' },
  treeMode: true,
  collapsible: true,
  showSavedFilters: false,
  rows: [
    { fields: [
      { key: 'brand', enabled: true, widthPct: GRID_QUARTER },
      { key: 'site', enabled: true, widthPct: GRID_QUARTER },
    ] },
    { fields: [
      { key: 'category', enabled: true, widthPct: GRID_QUARTER },
      { key: 'subcategory', enabled: true, widthPct: GRID_QUARTER },
      { key: 'segment', enabled: true, widthPct: GRID_QUARTER },
    ] },
    // Alcance temporal (Acotar por: cantidad de tomas / período): slot full-width del host.
    { fields: [
      { key: 'period', enabled: true, widthPct: GRID_FULL },
    ] },
  ],
};

/** Registry para lookup por sectionId. */
export const FILTER_BOX_CONFIGS: Record<string, FilterBoxConfig> = {
  [PUBLICACIONES_CONFIG.sectionId]: PUBLICACIONES_CONFIG,
  [ESTADISTICAS_CONFIG.sectionId]: ESTADISTICAS_CONFIG,
  [DATAHEALTH_HIDDEN_CONFIG.sectionId]: DATAHEALTH_HIDDEN_CONFIG,
  [TENDENCIAS_CONFIG.sectionId]: TENDENCIAS_CONFIG,
  [EXPORTAR_CONFIG.sectionId]: EXPORTAR_CONFIG,
};

export function getFilterBoxConfig(sectionId: string): FilterBoxConfig | undefined {
  return FILTER_BOX_CONFIGS[sectionId];
}
